import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCountUp } from "../../hooks/useCountUp";
import { FilterBar, SearchRow } from "../MarketTrad/shared";
import NlimsDataTable from "../shared/NlimsDataTable/NlimsDataTable";
import WorkforceCreateModal from "./WorkforceCreateModal";
import { useAuth } from "../../context/AuthContext";
import { canCreateModule, MODULE_KEYS } from "../../config/rbacConfig";
import {
  cooperativesApi,
  livestockActorsApi,
  workforceDeploymentsApi,
} from "../../api/workforceApi";
import { useLocationOptions } from "../Admin/shared/useLocationOptions";
import { ActionDropdown, anchorRect, QuickConfirmModal, actionCellHtml, WORKFLOW_RULES } from "../shared/RowActionMenu";
import useRowWorkflowActions from "../shared/useRowWorkflowActions";

// â”€â”€â”€ Shared helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function parseStatValue(raw) {
  const s = String(raw).trim();
  const kMatch = s.match(/^([\d.]+)([kK])$/);
  if (kMatch) {
    const dec = (kMatch[1].split('.')[1] || '').length;
    const end = Math.round(parseFloat(kMatch[1]) * 1000);
    return { end, format: (n) => `${(n / 1000).toFixed(dec)}${kMatch[2]}` };
  }
  const pctMatch = s.match(/^([\d.]+)%$/);
  if (pctMatch) return { end: Math.round(parseFloat(pctMatch[1])), format: (n) => `${n}%` };
  const numMatch = s.match(/^[\d,]+$/);
  if (numMatch) return { end: parseInt(s.replace(/,/g, ''), 10), format: (n) => n.toLocaleString() };
  return { end: 0, format: () => s };
}

const STATUS_STYLE = {
  Active:        'background:#dcfce7;color:#15803d;',
  Inactive:      'background:#fee2e2;color:#dc2626;',
  Suspended:     'background:#fef3c7;color:#b45309;',
  Pending:       'background:#fef3c7;color:#b45309;border:1px solid #fcd34d;',
  Rejected:      'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;',
  Verified:      'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
  Completed:     'background:#dcfce7;color:#15803d;',
  'On Leave':    'background:#dbeafe;color:#1d4ed8;border:1px solid #93c5fd;',
  Overdue:       'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;',
  Defaulted:     'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;',
  Current:       'background:#dbeafe;color:#1d4ed8;border:1px solid #93c5fd;',
  Restructured:  'background:#f3f4f6;color:#6b7280;',
  'Under Review':'background:#fef3c7;color:#b45309;border:1px solid #fcd34d;',
  Lapsed:        'background:#f3f4f6;color:#6b7280;',
};
function statusHtml(data, type) {
  if (type !== 'display') return data ?? '';
  const style = STATUS_STYLE[data] || 'background:#f3f4f6;color:#6b7280;';
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:6px;font-size:11px;font-weight:600;${style}">${data || '-'}</span>`;
}

function useStateLabelOptions() {
  const { stateOptions } = useLocationOptions();
  return useMemo(
    () => stateOptions.map((option) => option?.label ?? option?.value ?? option).filter(Boolean),
    [stateOptions]
  );
}

// â”€â”€â”€ Shared modal atoms â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function getRecordUuid(row) {
  return row?.uuid ?? row?.id ?? null;
}

function percentOf(count, total) {
  if (!total) return 0;
  return Math.round((Number(count) / Number(total)) * 100);
}

function toDateValue(value) {
  if (!value) return null;
  const text = String(value).trim();
  if (!text) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const parsed = new Date(`${text}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const isoMatch = text.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (isoMatch) {
    const [, d, m, y] = isoMatch;
    const parsed = new Date(`${y}-${m}-${d}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function daysUntil(value) {
  const date = toDateValue(value);
  if (!date) return null;
  const diff = date.getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// TAB 3 - Workforce Deployment Records
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function DeployIcon({ type }) {
  const cls = "w-8 h-8 text-green-600";
  if (type === "doc") return (
    <svg className={cls} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
  if (type === "pct") return (
    <svg className={cls} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0c1.1.128 1.907 1.077 1.907 2.185z" />
    </svg>
  );
  return (
    <svg className={cls} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
function DeployStatCard({ label, value, sub, icon, color }) {
  const valueColor = color === "amber" ? "text-amber-500" : color === "green" ? "text-green-600" : "text-gray-900";
  const { end, format } = parseStatValue(value);
  const { count, ref } = useCountUp(end);
  return (
    <div ref={ref} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 flex-1">
      <DeployIcon type={icon} />
      <div>
        <p className={`text-2xl font-black leading-none ${valueColor}`}>{end > 0 ? format(count) : value}</p>
        <p className="text-[11px] text-gray-500 mt-1 leading-tight">{sub}</p>
      </div>
    </div>
  );
}

const DEPLOY_ACTOR_TYPE_OPTS = ['Veterinarian','Animal Scientist','Extension Worker','Livestock Inspector','Abattoir Officer','Vet Paraprofessional','CAHW','Cooperative Officer','Trader','Farmer Producer','Processor','Transporter','Input Supplier','Researcher','Other'];
const DEPLOY_STATUS_OPTS     = ['Active','Inactive','On Leave','Completed'];
const DEPLOY_TYPE_OPTS       = ['Permanent','Temporary','Field Mission','Training Attachment','Emergency'];


const DEPLOY_EMPTY = { actorType: '', status: '', state: '', deploymentType: '' };

const DEPLOY_COLUMNS = [
  { title: 'Actor Name',          data: 'actorName',          sortable: true },
  { title: 'Actor Type',          data: 'actorType',          sortable: true },
  { title: 'Status',              data: 'status',             sortable: true, render: statusHtml },
  { title: 'Posting Institution', data: 'postingInstitution', sortable: true },
  { title: 'State',               data: 'state',              sortable: true },
  { title: 'LGA',                 data: 'lga',                sortable: true },
  { title: 'Start Date',          data: 'startDate',          sortable: true },
  { title: 'End Date',            data: 'endDate',            sortable: true },
  { title: 'Supervisor',          data: 'supervisor',         sortable: true },
  { title: 'Deployment Type',     data: 'deploymentType',     sortable: true },
  { title: 'Mobility Status',     data: 'mobilityStatus',     sortable: true },
  { title: 'Action',              data: null,                 sortable: false, render: () => actionCellHtml() },
];

export function WorkforceDeployment() {
  const navigate = useNavigate();
  const { user, permissions = [] } = useAuth();
  const roleId = user?.roleId ?? '';
  const canCreate = canCreateModule(permissions, MODULE_KEYS.WORKFORCE) && roleId !== 'R02' && roleId !== 'R06';
  const canEdit = roleId !== 'R02' && roleId !== 'R06';
  const canDelete = roleId === 'R01' || roleId === 'R04' || roleId === 'R05';
  const stateOptions = useStateLabelOptions();
  const [ownData,    setOwnData]    = useState([]);
  const [registeredActors, setRegisteredActors] = useState([]);
  const [filters,    setFilters]    = useState(DEPLOY_EMPTY);
  const [search,     setSearch]     = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [actionMenu, setActionMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const loadDeployments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [deploymentsPayload, actorsPayload] = await Promise.all([
        workforceDeploymentsApi.listAll({ per_page: 200 }),
        livestockActorsApi.listAll({ per_page: 200 }),
      ]);
      setOwnData(deploymentsPayload.items || []);
      setRegisteredActors(actorsPayload.items || []);
    } catch (err) {
      setOwnData([]);
      setRegisteredActors([]);
      setError(err?.response?.data?.message || err?.message || 'Failed to load workforce deployments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDeployments();
  }, [loadDeployments, canDelete]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ownData.filter(r => {
      const matchSearch = !q || Object.values(r).some(v => typeof v === 'string' && v.toLowerCase().includes(q));
      const matchType   = !filters.actorType      || r.actorType      === filters.actorType;
      const matchStatus = !filters.status         || r.status         === filters.status;
      const matchState  = !filters.state          || r.state          === filters.state;
      const matchDeploy = !filters.deploymentType || r.deploymentType === filters.deploymentType;
      return matchSearch && matchType && matchStatus && matchState && matchDeploy;
    });
  }, [ownData, filters, search]);

  const stats = useMemo(() => {
    const activeDeployments = ownData.filter((row) => String(row.status).toLowerCase() === 'active').length;
    const activeDeploymentActors = new Set(
      ownData
        .filter((row) => String(row.status).toLowerCase() === 'active')
        .map((row) => row.livestock_actor_uuid)
        .filter(Boolean)
    );
    const undeployedRegisteredActors = registeredActors.filter(
      (actor) => !activeDeploymentActors.has(actor.uuid)
    ).length;
    const vetStates = new Set(
      ownData
        .filter((row) => {
          const actorType = String(row.actorType || row.role_category || '').toLowerCase();
          const isVet = actorType.includes('veterinarian') || actorType.includes('vet');
          return isVet && String(row.status).toLowerCase() === 'active';
        })
        .map((row) => row.state)
        .filter(Boolean)
    );
    const allStates = [...new Set([
      ...stateOptions,
      ...registeredActors.map((actor) => actor.state).filter(Boolean),
      ...ownData.map((row) => row.state).filter(Boolean),
    ])];
    const statesWithNoVetDeployment = allStates.filter((state) => !vetStates.has(state)).length;
    const expiringPostings = ownData.filter((row) => {
      if (String(row.status).toLowerCase() !== 'active') return false;
      const remaining = daysUntil(row.end_date || row.endDate);
      return remaining !== null && remaining <= 30 && remaining >= 0;
    }).length;

    return [
      { label: 'Total Active Deployments', value: String(activeDeployments), sub: 'Total active workforce postings', icon: 'doc', color: 'black' },
      { label: 'Undeployed Registered Actors', value: String(undeployedRegisteredActors), sub: 'Registered but no active deployment', icon: 'doc', color: 'black' },
      { label: 'States with No Vet Deployment', value: String(statesWithNoVetDeployment), sub: 'States with zero active vets', icon: 'pct', color: 'amber' },
      { label: 'Expiring Postings (30 days)', value: String(expiringPostings), sub: 'End date within the next 30 days', icon: 'clock', color: 'green' },
    ];
  }, [ownData, registeredActors, stateOptions]);

  const deployFilters = useMemo(() => ([
    { key: 'actorType',      label: 'Actor Type',      options: DEPLOY_ACTOR_TYPE_OPTS, defaultLabel: 'All' },
    { key: 'status',         label: 'Status',          options: DEPLOY_STATUS_OPTS,     defaultLabel: 'All' },
    { key: 'state',          label: 'State',           options: stateOptions,           defaultLabel: 'All' },
    { key: 'deploymentType', label: 'Deployment Type', options: DEPLOY_TYPE_OPTS,       defaultLabel: 'All' },
  ]), [stateOptions]);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action == null) setActionMenu({ row, rect });
  }, []);
  const workflow = useRowWorkflowActions({
    api: workforceDeploymentsApi,
    rules: WORKFLOW_RULES.workforceDeployments,
    moduleKey: 'workforce',
    roleId,
    onChanged: loadDeployments,
    onError: setError,
    labelOf: (row) => row?.actorName || row?.actor_name || row?.postingInstitution,
  });

  const handleDelete    = useCallback(async (row) => {
    if (!canDelete) return;
    try {
      const uuid = getRecordUuid(row);
      if (!uuid) throw new Error('Missing deployment uuid.');
      await workforceDeploymentsApi.remove(uuid);
      await loadDeployments();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete deployment.');
    }
  }, [loadDeployments, canDelete]);
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try { await handleDelete(deleteTarget); setDeleteTarget(null); }
    finally { setDeleteBusy(false); }
  }, [deleteTarget, handleDelete]);

  return (
    <div>
      <FilterBar filters={deployFilters} filterValues={filters} onFilterChange={(k, v) => setFilters(p => ({ ...p, [k]: v }))} onApply={() => {}} />
      <SearchRow searchValue={search} onSearch={setSearch} onCreate={() => navigate("/workforce-institutional/new?subModule=deployment")} canCreate={canCreate} />
      <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-4">
        {stats.map((s, i) => <DeployStatCard key={i} {...s} />)}
      </div>
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {loading && (
        <div className="mb-4 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">
          Loading workforce deployment records...
        </div>
      )}
      <NlimsDataTable tableId="workforce-deployment-table" columns={DEPLOY_COLUMNS} data={filtered} serverSide={false} hideSearch pageLength={10} enableRowSelection onRowAction={handleRowAction} />
      {workflow.busy && (
        <div className="mb-4 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">{workflow.busy}</div>
      )}
      {actionMenu && !deleteBusy && !workflow.busy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.workforceDeployments}
          onClose={() => setActionMenu(null)}
          onView={() => { navigate(`/workforce-institutional/view/${getRecordUuid(actionMenu.row)}?subModule=deployment`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onEdit={() => { navigate(`/workforce-institutional/edit/${getRecordUuid(actionMenu.row)}?subModule=deployment`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onSubmit={() => workflow.run(actionMenu.row, 'submitted')}
          onValidate={() => workflow.run(actionMenu.row, 'validated')}
          onApprove={() => workflow.run(actionMenu.row, 'approved')}
          onReject={() => { workflow.askReject(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}
      {workflow.rejectModal}
      {deleteTarget && (
        <QuickConfirmModal
          title="Delete Deployment"
          message={`Permanently delete "${deleteTarget.actorName || 'this deployment'}"? This cannot be undone.`}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={deleteBusy}
        />
      )}
      <WorkforceCreateModal isOpen={createOpen} onClose={() => setCreateOpen(false)} onCreate={loadDeployments} defaultSubModule="deployment" />
    </div>
  );
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// TAB 5 - Cooperatives & Organization
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function CoopStatCard({ label, value, sub, color }) {
  const valueColor = color === "green" ? "text-green-600" : "text-gray-900";
  const { end, format } = parseStatValue(value);
  const { count, ref } = useCountUp(end);
  return (
    <div ref={ref} className="bg-white border border-gray-200 rounded-xl p-4 flex-1">
      <p className="text-[11px] text-gray-500">{label}</p>
      <p className={`text-2xl font-black leading-none mt-1 ${valueColor}`}>{end > 0 ? format(count) : value}</p>
      <p className="text-[9px] text-gray-400 mt-1 whitespace-pre-line leading-snug">{sub}</p>
    </div>
  );
}

const REG_AUTH_OPTS    = ['CAC','FMLD','State Ministry','Cooperative Society','Other'];
const BANK_STATUS_OPTS = ['Active','Inactive','Pending'];


const COOP_EMPTY = { state: '', registeringAuthority: '', bankAccountStatus: '' };

const COOP_COLUMNS = [
  { title: 'Organisation Name',     data: 'orgName',              sortable: true },
  { title: 'Organisation Type',     data: 'organisationType',     sortable: true, defaultContent: '' },
  { title: 'Reg Number',            data: 'regNumber',            sortable: true },
  { title: 'State',                 data: 'state',                sortable: true },
  { title: 'LGA',                   data: 'lga',                  sortable: true },
  { title: 'Ward / Community',      data: 'wardCommunity',        sortable: true },
  { title: 'Total Members',         data: 'totalMembers',         sortable: true },
  { title: 'Active Members',        data: 'activeMembers',        sortable: true },
  { title: 'Registering Authority', data: 'registeringAuthority', sortable: true },
  { title: 'Date of Registration',  data: 'dateOfRegistration',   sortable: true },
  { title: 'Bank Account Status',   data: 'bankAccountStatus',    sortable: true, render: statusHtml },
  { title: 'Status',                data: 'status',               sortable: true, render: statusHtml },
  { title: 'Primary Commodity',     data: 'primaryCommodity',     sortable: true, defaultContent: '' },
  { title: 'Primary Activities',    data: 'primaryActivities',    sortable: true },
  { title: 'Action',                data: null,                   sortable: false, render: () => actionCellHtml() },
];

export function Cooperatives() {
  const navigate = useNavigate();
  const { user, permissions = [] } = useAuth();
  const roleId = user?.roleId ?? '';
  const canCreate = canCreateModule(permissions, MODULE_KEYS.WORKFORCE) && roleId !== 'R02' && roleId !== 'R06';
  const canEdit = roleId !== 'R02' && roleId !== 'R06';
  const canDelete = roleId === 'R01' || roleId === 'R04' || roleId === 'R05';
  const stateOptions = useStateLabelOptions();
  const coopFilters = useMemo(() => ([
    { key: 'state',               label: 'State',                options: stateOptions,     defaultLabel: 'All' },
    { key: 'registeringAuthority',label: 'Registering Authority',options: REG_AUTH_OPTS,    defaultLabel: 'All' },
    { key: 'bankAccountStatus',   label: 'Bank Account Status',  options: BANK_STATUS_OPTS, defaultLabel: 'All' },
  ]), [stateOptions]);
  const [ownData,    setOwnData]    = useState([]);
  const [filters,    setFilters]    = useState(COOP_EMPTY);
  const [search,     setSearch]     = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [actionMenu, setActionMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const loadCooperatives = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await cooperativesApi.listAll({ per_page: 200 });
      setOwnData(payload.items || []);
    } catch (err) {
      setOwnData([]);
      setError(err?.response?.data?.message || err?.message || 'Failed to load cooperative records.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCooperatives();
  }, [loadCooperatives, canDelete]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ownData.filter(r => {
      const matchSearch = !q || Object.values(r).some(v => typeof v === 'string' && v.toLowerCase().includes(q));
      const matchState  = !filters.state                || r.state               === filters.state;
      const matchAuth   = !filters.registeringAuthority || r.registeringAuthority === filters.registeringAuthority;
      const matchBank   = !filters.bankAccountStatus    || r.bankAccountStatus   === filters.bankAccountStatus;
      return matchSearch && matchState && matchAuth && matchBank;
    });
  }, [ownData, filters, search]);

  const stats = useMemo(() => {
    const total = ownData.length;
    const activeCount = ownData.filter((row) => String(row.status).toLowerCase() === 'active').length;
    const activeMembers = ownData.reduce((sum, row) => sum + (Number(row.activeMembers ?? row.active_members ?? 0) || 0), 0);
    const bankCount = ownData.filter((row) => String(row.bankAccountStatus).toLowerCase() === 'active').length;

    return [
      { label: 'Total Registered Organisations', value: String(total), sub: 'Total organisations registered', color: 'green' },
      { label: 'Active Organisations', value: `${percentOf(activeCount, total)}%`, sub: 'Green >=80% / Amber 60-79% / Red <60%', color: 'green' },
      { label: 'Total Active Membership', value: String(activeMembers), sub: 'Total active members across organisations', color: 'green' },
      { label: 'Organisations with Bank Account', value: `${percentOf(bankCount, total)}%`, sub: 'Green >=70% / Amber 50-69% / Red <50%', color: 'green' },
    ];
  }, [ownData]);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === 'menu' || action == null) setActionMenu({ row, rect });
  }, []);
  const workflow = useRowWorkflowActions({
    api: cooperativesApi,
    rules: WORKFLOW_RULES.cooperatives,
    moduleKey: 'workforce',
    roleId,
    onChanged: loadCooperatives,
    onError: setError,
    labelOf: (row) => row?.orgName || row?.organisation_name || row?.cooperative_name,
  });

  const handleDelete    = useCallback(async (row) => {
    if (!canDelete) return;
    try {
      const uuid = getRecordUuid(row);
      if (!uuid) throw new Error('Missing cooperative uuid.');
      await cooperativesApi.remove(uuid);
      await loadCooperatives();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete cooperative record.');
    }
  }, [loadCooperatives, canDelete]);
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try { await handleDelete(deleteTarget); setDeleteTarget(null); }
    finally { setDeleteBusy(false); }
  }, [deleteTarget, handleDelete]);

  return (
    <div>
      <div className="grid grid-cols-1 gap-4 mb-5 md:grid-cols-4">
        {stats.map((s, i) => <CoopStatCard key={i} {...s} />)}
      </div>
      <FilterBar filters={coopFilters} filterValues={filters} onFilterChange={(k, v) => setFilters(p => ({ ...p, [k]: v }))} onApply={() => {}} />
      <SearchRow searchValue={search} onSearch={setSearch} onCreate={() => navigate("/workforce-institutional/new?subModule=coop")} canCreate={canCreate} />
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {loading && (
        <div className="mb-4 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">
          Loading cooperative records...
        </div>
      )}
      <NlimsDataTable tableId="cooperatives-table" columns={COOP_COLUMNS} data={filtered} serverSide={false} hideSearch pageLength={10} enableRowSelection onRowAction={handleRowAction} />
      {workflow.busy && (
        <div className="mb-4 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">{workflow.busy}</div>
      )}
      {actionMenu && !deleteBusy && !workflow.busy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.cooperatives}
          onClose={() => setActionMenu(null)}
          onView={() => { navigate(`/workforce-institutional/view/${getRecordUuid(actionMenu.row)}?subModule=coop`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onEdit={() => { navigate(`/workforce-institutional/edit/${getRecordUuid(actionMenu.row)}?subModule=coop`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onSubmit={() => workflow.run(actionMenu.row, 'submitted')}
          onValidate={() => workflow.run(actionMenu.row, 'validated')}
          onApprove={() => workflow.run(actionMenu.row, 'approved')}
          onReject={() => { workflow.askReject(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}
      {workflow.rejectModal}
      {deleteTarget && (
        <QuickConfirmModal
          title="Delete Cooperative"
          message={`Permanently delete "${deleteTarget.orgName || 'this cooperative'}"? This cannot be undone.`}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={deleteBusy}
        />
      )}
      <WorkforceCreateModal isOpen={createOpen} onClose={() => setCreateOpen(false)} onCreate={loadCooperatives} defaultSubModule="coop" />
    </div>
  );
}

